import { useState, useCallback } from 'react'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useUpdateUserStandardScope } from '@/shared/hooks/api/userStandards'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useStandardSectionExtraction } from '../hooks/useStandardSectionExtraction'
import type { StandardDetailSectionProps } from '../standardDetailSectionProps'
import { StandardSectionEmptyState } from './StandardSectionEmptyState'
import { StandardSectionExtractControls } from './StandardSectionExtractControls'

export type StandardScopeSectionProps = StandardDetailSectionProps

export function StandardScopeSection({
  standard,
  hasPdf,
  isPdfUploading,
  onUploadPdf,
}: StandardScopeSectionProps) {
  const extraction = useStandardSectionExtraction(standard, 'scope')
  const { t } = useTranslation('standardsLibrary')
  const updateScope = useUpdateUserStandardScope(standard.id)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [scopeDraft, setScopeDraft] = useState(standard.scope ?? '')

  const openDialog = useCallback(() => {
    setScopeDraft(standard.scope ?? '')
    setDialogOpen(true)
  }, [standard.scope])

  const closeDialog = useCallback(() => {
    setDialogOpen(false)
  }, [])

  const handleSave = useCallback(() => {
    updateScope.mutate(
      { scope: scopeDraft },
      {
        onSuccess: () => {
          closeDialog()
          toast.success(t('detail.scopeSaveSuccess'))
        },
        onError: (err) => {
          toast.error(err?.message ?? t('detail.saveError'))
        },
      }
    )
  }, [scopeDraft, updateScope, closeDialog, t])

  const displayScope = standard.scope?.trim() ?? null

  return (
    <>
      <Card inert={extraction.extractDialogOpen || undefined}>
        <CardHeader className='flex flex-row items-center justify-between gap-4 pb-2'>
          <CardTitle>{t('detail.itehScope')}</CardTitle>
          <div className='flex flex-wrap items-center gap-2'>
            <StandardSectionExtractControls
              hasPdf={hasPdf}
              extraction={extraction}
            />
            {displayScope != null && (
              <Button variant='outline' size='sm' onClick={openDialog}>
                {t('detail.edit')}
              </Button>
            )}
          </div>
        </CardHeader>
        {displayScope ? (
          <div className='px-4 py-2'>
            <p className='max-h-[250px] overflow-y-auto text-sm whitespace-pre-wrap'>
              {displayScope}
            </p>
          </div>
        ) : (
          <div>
            <StandardSectionEmptyState
              icon={<FileText className='size-10' aria-hidden />}
              title={t('detail.empty.scope.title')}
              description={
                hasPdf
                  ? t('detail.empty.scope.descriptionWithPdf')
                  : t('detail.empty.scope.descriptionNoPdf')
              }
              hasPdf={hasPdf}
              isPdfUploading={isPdfUploading}
              extraction={extraction}
              onUploadPdf={onUploadPdf}
              onManualAction={openDialog}
              manualActionLabel={t('detail.edit')}
            />
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className='max-h-[90vh] max-w-2xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{t('detail.editScope')}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            {updateScope.isError && (
              <Alert variant='destructive'>
                <AlertDescription>
                  {updateScope.error?.message ?? t('detail.saveError')}
                </AlertDescription>
              </Alert>
            )}
            <Textarea
              value={scopeDraft}
              onChange={(ev) => setScopeDraft(ev.target.value)}
              placeholder={t('detail.scopePlaceholder')}
              className='min-h-[200px] resize-y'
              aria-label={t('detail.itehScope')}
            />
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={closeDialog}
              disabled={updateScope.isPending}
            >
              {t('detail.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={updateScope.isPending}>
              {updateScope.isPending ? 'Saving…' : t('detail.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
